/**
 * @description Invoice calculation, formatting, and line-item type helpers.
 * @dependencies None (pure date/money display utilities).
 * @notes Generic Quick-add "Sales" is persisted as item_type=product
 * (invoice_items CHECK has no 'sales'). Catalog lines copy products.item_type;
 * bundle/unknown map to product because the line CHECK has no bundle.
 * Catalog products with variants must send variantId on save (LYN-107).
 * Invoice/estimate Item typeahead nests variants under the parent (LYN-377);
 * resolveCatalogLineIdentityFromVariant is the explicit picker path.
 * DATE columns use parseDateSafely / formatInvoiceDate; timestamptz created_at
 * uses formatTimestampDate (do not slice the UTC YYYY-MM-DD prefix).
 * Invoice overdue / due-today use the company primary-location IANA timezone
 * (Square model), not the viewer's laptop timezone.
 */

/** Fallback when locations.timezone is missing or not a valid IANA name. */
export const DEFAULT_COMPANY_TIMEZONE = 'America/New_York';

/** @type {readonly string[]} Values allowed by invoice_items.item_type CHECK */
export const INVOICE_LINE_ITEM_TYPES = Object.freeze([
  'product',
  'service',
  'shipping',
  'discount',
  'tax',
  'custom'
]);

/**
 * Map a Quick-add generic label to a persisted invoice_items.item_type.
 * @param {string} label - UI label (Sales, Service, Shipping)
 * @returns {string} Allowed item_type
 */
export function resolveGenericLineItemType(label) {
  const key = String(label || '').trim().toLowerCase();
  if (key === 'sales') return 'product';
  if (key === 'service') return 'service';
  if (key === 'shipping') return 'shipping';
  return 'product';
}

/**
 * Persist catalog line item_type from the product record.
 * @param {{ itemType?: string, item_type?: string }|null|undefined} product
 * @returns {string} Allowed item_type (bundle/unknown → product)
 */
export function resolveCatalogLineItemType(product) {
  const raw = String(product?.itemType ?? product?.item_type ?? 'product')
    .trim()
    .toLowerCase();
  if (raw === 'service') return 'service';
  if (raw === 'shipping') return 'shipping';
  return 'product';
}

/**
 * Calculate line item totals
 * @param {Object} item - Line item data
 * @returns {Object} Calculated values
 */
export const calculateLineItem = (item) => {
  const quantity = parseFloat(item.quantity) || 0;
  const unitPrice = parseFloat(item.unitPrice) || 0;
  const discountPercent = parseFloat(item.discountPercent) || 0;
  const discountAmount = parseFloat(item.discountAmount) || 0;
  
  // Calculate base subtotal
  let subtotal = quantity * unitPrice;
  
  // Apply discounts
  if (discountPercent > 0) {
    subtotal = subtotal * (1 - discountPercent / 100);
  }
  if (discountAmount > 0) {
    subtotal = subtotal - discountAmount;
  }
  
  // Ensure subtotal is not negative
  subtotal = Math.max(0, subtotal);
  
  return {
    subtotal: subtotal,
    total: subtotal // Will be updated with tax in invoice calculations
  };
};

/**
 * Calculate invoice totals with proportional discount allocation
 * @param {Array} items - Array of line items
 * @param {Object} invoiceData - Invoice data including tax rate, discount, shipping
 * @returns {Object} Calculated totals
 */
export const calculateInvoiceTotals = (items, invoiceData = {}) => {
  const taxRate = parseFloat(invoiceData.taxRate) || 0;
  const discountType = invoiceData.discountType;
  const discountValue = parseFloat(invoiceData.discountValue) || 0;
  const shippingCost = parseFloat(invoiceData.shippingCost) || 0;
  
  // Calculate subtotal from all items
  let subtotal = 0;
  let taxableSubtotal = 0;
  
  items.forEach(item => {
    const itemCalc = calculateLineItem(item);
    subtotal += itemCalc.subtotal;
    
    // Add to taxable subtotal if item is taxable
    if (item.isTaxable) {
      taxableSubtotal += itemCalc.subtotal;
    }
  });
  
  // Apply invoice-level discount. appliedDiscountAmount is Σ (manual $ + engine).
  let discountAmount = 0;
  if (invoiceData.appliedDiscountAmount != null) {
    discountAmount = parseFloat(invoiceData.appliedDiscountAmount) || 0;
  } else if (discountType === 'percentage' && discountValue > 0) {
    discountAmount = subtotal * (discountValue / 100);
  } else if (discountType === 'fixed_amount' && discountValue > 0) {
    discountAmount = discountValue;
  } else if (discountValue > 0 && !discountType) {
    // Default to fixed discount if no type specified but value exists
    discountAmount = discountValue;
  }
  
  // Calculate proportional discount allocation
  let taxableSubtotalAfterDiscount = taxableSubtotal;
  let taxAmount = 0;
  
  if (discountAmount > 0 && subtotal > 0) {
    // Calculate the proportion of taxable items to total
    const taxableRatio = taxableSubtotal / subtotal;
    
    // Allocate discount proportionally to taxable items
    const taxableDiscountAmount = discountAmount * taxableRatio;
    
    // Calculate taxable amount after proportional discount
    taxableSubtotalAfterDiscount = taxableSubtotal - taxableDiscountAmount;
    
    // Calculate tax on the proportionally discounted amount
    taxAmount = taxableSubtotalAfterDiscount * (taxRate / 100);
  } else {
    // No discount, calculate tax on full taxable subtotal
    taxAmount = taxableSubtotal * (taxRate / 100);
  }
  
  // Calculate final values
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);
  const totalAmount = discountedSubtotal + taxAmount + shippingCost;
  
  return {
    subtotal: subtotal,
    taxableSubtotal: taxableSubtotal,
    taxableSubtotalAfterDiscount: taxableSubtotalAfterDiscount,
    discountAmount: discountAmount,
    taxAmount: Math.max(0, taxAmount),
    shippingCost: shippingCost,
    totalAmount: Math.max(0, totalAmount)
  };
};

/**
 * Format invoice number
 * @param {number} number - Invoice number
 * @returns {string} Formatted invoice number
 */
export const formatInvoiceNumber = (number) => {
  if (!number) return '';
  if (typeof number === 'string' && number.startsWith('INV-')) {
    return number;
  }
  return `INV-${String(number).padStart(3, '0')}`;
};

/**
 * True for a calendar DATE (YYYY-MM-DD) or a DATE serialized as UTC midnight.
 * False for real timestamptz values (created_at), which must use local time.
 * @param {*} value
 * @returns {boolean}
 */
export const isDateOnlyOrUtcMidnight = (value) => {
  if (typeof value !== 'string') return false;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return true;
  return /^\d{4}-\d{2}-\d{2}T00:00:00(?:\.0+)?(?:Z|[+-]00:00)$/.test(value);
};

/**
 * Format a calendar DATE for display (due_date, issue_date).
 * DATE columns arrive as YYYY-MM-DD or 00:00:00.000Z — never shift those
 * through UTC. Real timestamps (created_at) use formatTimestampDate.
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatInvoiceDate = (date) => {
  if (!date) return '';
  const d = isDateOnlyOrUtcMidnight(date)
    ? parseDateSafely(date)
    : new Date(date);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a timestamptz (created_at) as the viewer's local calendar day.
 * Do not slice the UTC YYYY-MM-DD prefix — evening US saves are already
 * the next UTC date.
 * @param {string|Date} timestamp
 * @returns {string}
 */
export const formatTimestampDate = (timestamp) => {
  if (!timestamp) return '';
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date for input fields (YYYY-MM-DD) - Payment industry standard UTC handling
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date for input
 */
export const formatDateForInput = (date) => {
  if (!date) return '';
  const d = isDateOnlyOrUtcMidnight(date)
    ? parseDateSafely(date)
    : new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Create a date in local timezone for today (payment industry standard)
 * @returns {Date} Today's date in local timezone
 */
export const getTodayLocalDate = () => {
  const today = new Date();
  // Return new Date object set to start of today in local timezone
  return new Date(today.getFullYear(), today.getMonth(), today.getDate());
};

/**
 * Parse date string safely without timezone issues
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export const parseDateSafely = (dateString) => {
  if (!dateString) return null;

  // For YYYY-MM-DD format, parse as local date to avoid timezone shifts
  // Slice to 10 chars to handle ISO timestamps like "2026-04-28T00:00:00.000Z"
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Calculate due date based on payment terms
 * @param {Date} invoiceDate - Invoice date
 * @param {string} paymentTerms - Payment terms
 * @returns {Date} Due date
 */
export const calculateDueDate = (invoiceDate, paymentTerms) => {
  const date = typeof invoiceDate === 'string' ? parseDateSafely(invoiceDate) : new Date(invoiceDate);
  
  switch (paymentTerms) {
    case 'due_on_receipt':
      return date;
    case 'net_15':
      date.setDate(date.getDate() + 15);
      break;
    case 'net_30':
      date.setDate(date.getDate() + 30);
      break;
    case 'net_60':
      date.setDate(date.getDate() + 60);
      break;
    case 'net_90':
      date.setDate(date.getDate() + 90);
      break;
    default:
      date.setDate(date.getDate() + 30); // Default to net 30
  }
  
  return date;
};

/**
 * Get payment terms display text
 * @param {string} terms - Payment terms value
 * @returns {string} Display text
 */
export const getPaymentTermsDisplay = (terms) => {
  const termsMap = {
    'due_on_receipt': 'Due on Receipt',
    'net_15': 'Net 15',
    'net_30': 'Net 30',
    'net_60': 'Net 60',
    'net_90': 'Net 90'
  };
  return termsMap[terms] || terms;
};

/**
 * Resolve an IANA timezone; invalid names fall back to America/New_York.
 * @param {string} [timeZone]
 * @returns {string}
 */
export const resolveIanaTimeZone = (timeZone) => {
  if (typeof timeZone !== 'string' || !timeZone.trim()) {
    return DEFAULT_COMPANY_TIMEZONE;
  }
  const candidate = timeZone.trim();
  try {
    Intl.DateTimeFormat('en-US', { timeZone: candidate });
    return candidate;
  } catch {
    return DEFAULT_COMPANY_TIMEZONE;
  }
};

/**
 * Calendar YYYY-MM-DD for a DATE column or Date (local Y/M/D).
 * @param {string|Date|null|undefined} dateValue
 * @returns {string|null}
 */
export const toCalendarDateString = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  }
  if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) {
    const year = dateValue.getFullYear();
    const month = String(dateValue.getMonth() + 1).padStart(2, '0');
    const day = String(dateValue.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return null;
};

/**
 * Today's calendar date in an IANA timezone.
 * @param {string} [timeZone]
 * @param {Date} [now]
 * @returns {string} YYYY-MM-DD
 */
export const getTodayDateStringInTimeZone = (timeZone, now = new Date()) => {
  const tz = resolveIanaTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);
  const year = parts.find((part) => part.type === 'year')?.value;
  const month = parts.find((part) => part.type === 'month')?.value;
  const day = parts.find((part) => part.type === 'day')?.value;
  if (!year || !month || !day) return toCalendarDateString(getTodayLocalDate());
  return `${year}-${month}-${day}`;
};

/**
 * Determine if invoice is overdue in the company timezone.
 * @param {string|Date} dueDate - DATE column
 * @param {string} paymentStatus - Payment status
 * @param {string} [timeZone] - Primary location IANA TZ; defaults to America/New_York
 * @returns {boolean} True if overdue
 */
export const isInvoiceOverdue = (dueDate, paymentStatus, timeZone) => {
  if (paymentStatus === 'paid' || paymentStatus === 'voided') return false;
  const dueStr = toCalendarDateString(dueDate);
  if (!dueStr) return false;
  return dueStr < getTodayDateStringInTimeZone(timeZone);
};

/**
 * True when the DATE is today in the company timezone.
 * @param {string|Date} dueDate
 * @param {string} [timeZone]
 * @returns {boolean}
 */
export const isInvoiceDueToday = (dueDate, timeZone) => {
  const dueStr = toCalendarDateString(dueDate);
  if (!dueStr) return false;
  return dueStr === getTodayDateStringInTimeZone(timeZone);
};

/**
 * Whole calendar days past due in the company timezone.
 * @param {string|Date} dueDate
 * @param {string} [timeZone]
 * @param {Date} [now]
 * @returns {number}
 */
export const calendarDaysOverdue = (dueDate, timeZone, now = new Date()) => {
  const dueStr = toCalendarDateString(dueDate);
  if (!dueStr) return 0;
  const todayStr = getTodayDateStringInTimeZone(timeZone, now);
  const dueMs = Date.parse(`${dueStr}T00:00:00Z`);
  const todayMs = Date.parse(`${todayStr}T00:00:00Z`);
  return Math.max(0, Math.round((todayMs - dueMs) / 86400000));
};

/**
 * Get status color classes
 * @param {string} status - Invoice status
 * @param {string} paymentStatus - Payment status
 * @param {string} dueDate - Due date
 * @param {string} [timeZone] - Company primary-location IANA TZ
 * @returns {string} Tailwind CSS classes
 */
export const getInvoiceStatusColor = (status, paymentStatus, dueDate, timeZone) => {
  // Payment status takes precedence
  if (paymentStatus === 'paid') {
    return 'bg-green-100 text-green-800';
  }
  if (paymentStatus === 'partially_paid') {
    return 'bg-orange-100 text-orange-800';
  }
  
  // Check if overdue
  if (isInvoiceOverdue(dueDate, paymentStatus, timeZone)) {
    return 'bg-red-100 text-red-800';
  }
  
  // Document status colors
  const statusColors = {
    'open': 'bg-blue-100 text-blue-800',
    'void': 'bg-gray-100 text-gray-600'
  };

  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Get display status text
 * @param {string} status - Invoice status
 * @param {string} paymentStatus - Payment status
 * @param {string} dueDate - Due date
 * @param {string} [timeZone] - Company primary-location IANA TZ
 * @returns {string} Display text
 */
export const getInvoiceDisplayStatus = (status, paymentStatus, dueDate, timeZone) => {
  // Payment status takes precedence
  if (paymentStatus === 'paid') return 'Paid';
  if (paymentStatus === 'partially_paid') return 'Partially Paid';
  
  // Check if overdue
  if (isInvoiceOverdue(dueDate, paymentStatus, timeZone)) return 'Overdue';
  
  // Document status display
  const statusMap = {
    'open': 'Open',
    'void': 'Void'
  };

  return statusMap[status] || status;
};

/**
 * Validate invoice data before submission
 * @param {Object} invoiceData - Invoice data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateInvoice = (invoiceData) => {
  const errors = {};
  
  // Required fields
  if (!invoiceData.customerId) {
    errors.customer = 'Customer is required';
  }
  
  if (!invoiceData.invoiceDate) {
    errors.invoiceDate = 'Invoice date is required';
  }
  
  if (!invoiceData.dueDate) {
    errors.dueDate = 'Due date is required';
  }
  
  // Validate items
  const items = invoiceData.items || [];
  if (items.length === 0) {
    errors.items = 'At least one line item is required';
  } else {
    items.forEach((item, index) => {
      if (!item.itemName) {
        errors[`item_${index}_name`] = 'Item name is required';
      }
      if (!item.quantity || item.quantity <= 0) {
        errors[`item_${index}_quantity`] = 'Valid quantity is required';
      }
      if (!item.unitPrice) {
        errors[`item_${index}_price`] = 'Unit price is required';
      }
    });
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Get default invoice data
 * @returns {Object} Default invoice structure
 */
export const getDefaultInvoiceData = () => {
  const today = getTodayLocalDate();
  const dueDate = calculateDueDate(today, 'net_30');
  
  return {
    customerId: '',
    invoiceDate: formatDateForInput(today),
    dueDate: formatDateForInput(dueDate),
    paymentTerms: 'net_30',
    status: 'open',
    items: [],
    taxRate: 0,
    discountType: null,
    discountValue: 0,
    discountCode: '',
    shippingCost: 0,
    paymentOptions: ['credit_card', 'bank'],
    internalNotes: '',
    customerNotes: '',
    terms: '',
    footer: ''
  };
};

/**
 * Get default line item
 * @returns {Object} Default line item structure
 */
export const getDefaultLineItem = () => {
  return {
    itemName: '',
    itemType: 'product',
    description: '',
    date: formatDateForInput(getTodayLocalDate()),
    quantity: 1,
    unitPrice: 0,
    isTaxable: false,
    discountPercent: 0,
    discountAmount: 0,
    deductInventory: false,
    variantId: null,
    variantName: null
  };
};

/**
 * Active variants on a catalog product (from nested getProducts payload).
 * @param {object|null|undefined} product
 * @returns {Array<object>}
 */
export function getActiveCatalogVariants(product) {
  const list = Array.isArray(product?.variants) ? product.variants : [];
  return list.filter((row) => row && row.isActive !== false);
}

/**
 * Unit price in dollars from a catalog variant (amountCents preferred).
 * @param {object|null|undefined} product
 * @param {object|null|undefined} variant
 * @returns {number}
 */
export function catalogVariantUnitPrice(product, variant) {
  if (variant != null) {
    if (variant.amountCents != null) return Number(variant.amountCents) / 100;
    if (variant.price != null) return Number(variant.price);
  }
  return Number(product?.price) || 0;
}

/**
 * Display name for a catalog line (Product — Variant when variant named).
 * @param {object|null|undefined} product
 * @param {object|null|undefined} variant
 * @returns {string}
 */
export function catalogLineDisplayName(product, variant) {
  const productName = product?.name || '';
  if (variant?.name && productName) return `${productName} — ${variant.name}`;
  return productName;
}

/**
 * Sync identity when the merchant picks an explicit variant (LYN-377).
 * @param {object} product
 * @param {object} variant
 * @returns {object}
 */
export function resolveCatalogLineIdentityFromVariant(product, variant) {
  return {
    productId: product?.id ?? null,
    itemName: catalogLineDisplayName(product, variant),
    description: product?.description || '',
    itemType: resolveCatalogLineItemType(product),
    variantId: variant?.id ?? null,
    variantName: variant?.name || null,
    unitPrice: catalogVariantUnitPrice(product, variant)
  };
}

/**
 * Typeahead rows for invoice/estimate Product/Service picker (LYN-377).
 * Products with active variants emit a non-selectable header + nested variants.
 * Products without variants remain one-click selectable parents.
 *
 * @param {Array<object>} products
 * @param {string} [search]
 * @param {{ limit?: number }} [options]
 * @returns {Array<object>}
 */
export function buildCatalogPickerRows(products, search = '', options = {}) {
  const limit = Number(options.limit) > 0 ? Number(options.limit) : 8;
  const q = String(search || '').toLowerCase().trim();
  const list = Array.isArray(products) ? products : [];
  const matched = [];

  for (const product of list) {
    if (!product) continue;
    const variants = getActiveCatalogVariants(product);
    const productMatch = !q
      || (product.name && product.name.toLowerCase().includes(q))
      || (product.sku && String(product.sku).toLowerCase().includes(q));
    const matchingVariants = q
      ? variants.filter((row) => (row.name || '').toLowerCase().includes(q))
      : variants;
    if (!productMatch && matchingVariants.length === 0) continue;
    matched.push({ product, variants, productMatch, matchingVariants });
    if (matched.length >= limit) break;
  }

  const rows = [];
  for (const { product, variants, productMatch, matchingVariants } of matched) {
    if (variants.length === 0) {
      rows.push({
        kind: 'product',
        key: `product-${product.id}`,
        product,
        label: product.name || '',
        price: Number(product.price) || 0,
        sku: product.sku || null
      });
      continue;
    }

    rows.push({
      kind: 'product-header',
      key: `header-${product.id}`,
      product,
      label: product.name || '',
      sku: product.sku || null
    });

    const nested = (!q || productMatch) ? variants : matchingVariants;
    for (const variant of nested) {
      rows.push({
        kind: 'variant',
        key: `variant-${product.id}-${variant.id}`,
        product,
        variant,
        label: variant.name || 'Variant',
        price: catalogVariantUnitPrice(product, variant)
      });
    }
  }
  return rows;
}

/**
 * True when the Item field no longer matches the linked catalog display name.
 * @param {object|null|undefined} product
 * @param {string} itemName
 * @param {object|null|undefined} [item] - form line (variantId / variantName)
 * @returns {boolean}
 */
export function shouldClearCatalogLink(product, itemName, item = null) {
  if (!product) return true;
  const value = String(itemName || '');
  if (item?.variantId) {
    const expected = catalogLineDisplayName(product, {
      name: item.variantName || null
    });
    return value !== expected && value !== (product.name || '');
  }
  return value !== (product.name || '');
}

/**
 * Detach catalog product/variant identity without touching unitPrice.
 * Use for Quick-add Sales/Service/Shipping (LYN-377 deslop D1) — prior
 * quick-add only cleared productId + description and left the price alone.
 * @param {object} line
 * @returns {object}
 */
export function clearCatalogProductIdentity(line) {
  return {
    ...line,
    productId: null,
    variantId: null,
    variantName: null,
    description: ''
  };
}

/**
 * Clear catalog identity when the merchant unlinks the Item text by typing.
 * Also zeros unitPrice (pre-existing name-edit unlink behavior).
 * Do not use for Quick-add — use clearCatalogProductIdentity instead.
 * @param {object} line
 * @returns {object}
 */
export function clearCatalogLinkOnLine(line) {
  return {
    ...clearCatalogProductIdentity(line),
    unitPrice: 0
  };
}

/**
 * Attach catalog identity for an invoice/estimate line.
 * Prefer an explicit variant from the nested picker (LYN-377). When only a
 * parent is known, the first active variant is attached so LYN-107
 * createInvoice does not reject the line as VARIANT_REQUIRED.
 *
 * @param {object} product - Catalog product
 * @param {(productId: *) => Promise<object|Array>} [loadVariants]
 * @returns {Promise<object>} Fields to merge onto the form line
 */
export async function resolveCatalogLineIdentity(product, loadVariants) {
  const identity = {
    productId: product?.id ?? null,
    itemName: product?.name || '',
    description: product?.description || '',
    itemType: resolveCatalogLineItemType(product),
    variantId: null,
    variantName: null,
    unitPrice: Number(product?.price) || 0
  };
  if (!product?.id) {
    return identity;
  }

  let list = getActiveCatalogVariants(product);
  if (list.length === 0 && typeof loadVariants === 'function') {
    try {
      const result = await loadVariants(product.id);
      const raw = Array.isArray(result) ? result : (result?.variants || []);
      list = raw.filter((row) => row && row.isActive !== false);
    } catch {
      // Keep productId + remapped type; variantId stays null so save fail-closes
      return identity;
    }
  }

  const first = list[0];
  if (first) {
    return {
      ...identity,
      ...resolveCatalogLineIdentityFromVariant(product, first)
    };
  }
  return identity;
}

/**
 * Merge catalog identity onto one line using the latest items array.
 * Forms must call this from setItems(prev => ...) so an awaited variant
 * load cannot overwrite later line edits with a stale snapshot.
 *
 * @param {Array<object>} items
 * @param {number} index
 * @param {object} identity - resolveCatalogLineIdentity result
 * @returns {Array<object>}
 */
export function applyCatalogIdentityToLine(items, index, identity) {
  const list = Array.isArray(items) ? items : [];
  if (!list[index]) return list;
  const next = list.slice();
  next[index] = {
    ...next[index],
    ...identity,
    quantity: next[index].quantity || 1,
    deductInventory: true
  };
  const calc = calculateLineItem(next[index]);
  next[index] = {
    ...next[index],
    subtotal: calc.subtotal,
    total: calc.total
  };
  return next;
}

/**
 * Persist invoice/estimate line payload, including variant identity.
 *
 * @param {object} item - Form line
 * @param {number} index - Sort order source
 * @returns {object} API line item
 */
export function toDocumentLineItemPayload(item, index) {
  const quantity = parseFloat(item.quantity) || 0;
  const unitPrice = parseFloat(item.unitPrice) || 0;
  const discountPercent = parseFloat(item.discountPercent) || 0;
  const discountAmount = parseFloat(item.discountAmount) || 0;
  const subtotal = quantity * unitPrice;
  const discountValue = discountPercent > 0 ? (subtotal * discountPercent / 100) : discountAmount;
  const payload = {
    productId: item.productId,
    itemName: item.itemName,
    itemType: item.itemType,
    description: item.description,
    date: item.date,
    quantity,
    unitPrice,
    isTaxable: item.isTaxable,
    discountPercent,
    discountAmount,
    subtotal,
    total: subtotal - discountValue,
    deductInventory: item.deductInventory,
    sortOrder: index + 1
  };
  if (item.variantId) payload.variantId = item.variantId;
  if (item.variantName) payload.variantName = item.variantName;
  if (item.customAmount !== undefined && item.customAmount !== null) {
    payload.customAmount = item.customAmount;
  }
  return payload;
}

/**
 * Line subtotal in cents from form items (qty × unitPrice).
 * @param {Array<{quantity?: number, unitPrice?: number}>} items
 * @returns {number}
 */
export const lineItemsSubtotalCents = (items) => {
  return Math.round((items || []).reduce((sum, item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unitPrice) || 0;
    return sum + (quantity * unitPrice);
  }, 0) * 100);
};

/**
 * Cap a manual $ against remaining subtotal after engine rows.
 * @param {number} subtotalCents
 * @param {number} engineCents
 * @param {number} manualCents
 * @returns {number}
 */
export const capManualDiscountCents = (subtotalCents, engineCents, manualCents) => {
  const remaining = Math.max(
    0,
    (Number(subtotalCents) || 0) - Math.max(0, Number(engineCents) || 0)
  );
  return Math.min(Math.max(0, Number(manualCents) || 0), remaining);
};

/**
 * $ Discount field is always dollars. Leftover percentage headers use
 * discountAmount (already dollars from discount_amount_cents).
 * @param {{discountType?: string, discountValue?: number, discountAmount?: number}} doc
 * @returns {{discountType: string|null, discountValue: number}}
 */
export const hydrateManualDiscount = (doc = {}) => {
  if (doc.discountType === 'percentage') {
    return {
      discountType: 'fixed_amount',
      discountValue: Number(doc.discountAmount) || 0
    };
  }
  return {
    discountType: doc.discountType || null,
    discountValue: Number(doc.discountValue) || 0
  };
};

/**
 * Persist the $ Discount field as fixed_amount only.
 * @param {number|string} discountValue
 * @returns {{discountType: string|null, discountValue: number}}
 */
export const persistManualDiscount = (discountValue) => {
  const value = Number(discountValue) || 0;
  if (value <= 0) {
    return { discountType: null, discountValue: 0 };
  }
  return { discountType: 'fixed_amount', discountValue: value };
};

/**
 * Named applied discount lines for totals. Generic "Discount" only when
 * there are zero applied rows (legacy header-only docs).
 * @param {Array<object>|null|undefined} appliedDiscounts
 * @param {number} [legacyAmount] header dollars when no applied rows
 * @returns {Array<{title: string, amount: number}>}
 */
export const namedDiscountLines = (appliedDiscounts, legacyAmount = 0) => {
  const rows = Array.isArray(appliedDiscounts) ? appliedDiscounts : [];
  if (rows.length > 0) {
    return rows
      .map((row) => ({
        title: row.title || 'Discount',
        amount: Number(row.amountCents ?? row.amount_cents ?? 0) / 100
      }))
      .filter((row) => row.amount > 0);
  }
  const legacy = Number(legacyAmount) || 0;
  return legacy > 0 ? [{ title: 'Discount', amount: legacy }] : [];
};

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount || 0);
};

/**
 * Round a number to a specified number of decimal places
 * @param {number} value - The value to round
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {number} Rounded value
 */
export const roundToDecimal = (value, decimals = 2) => {
  return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Safe comparison for currency amounts (handles floating point precision issues)
 * @param {number} amount1 - First amount
 * @param {number} amount2 - Second amount
 * @param {number} tolerance - Tolerance for comparison (default: 0.001)
 * @returns {boolean} True if amounts are effectively equal
 */
export const currencyEquals = (amount1, amount2, tolerance = 0.001) => {
  return Math.abs(amount1 - amount2) < tolerance;
};

/**
 * Safe greater than comparison for currency amounts
 * @param {number} amount1 - First amount
 * @param {number} amount2 - Second amount
 * @param {number} tolerance - Tolerance for comparison (default: 0.001)
 * @returns {boolean} True if amount1 > amount2 (accounting for precision)
 */
export const currencyGreaterThan = (amount1, amount2, tolerance = 0.001) => {
  return amount1 - amount2 > tolerance;
};

/**
 * Calculate remaining balance with proper rounding
 * @param {number} totalAmount - Total invoice amount
 * @param {number} amountPaid - Amount already paid
 * @returns {number} Remaining balance rounded to 2 decimal places
 */
export const calculateRemainingBalance = (totalAmount, amountPaid) => {
  const total = parseFloat(totalAmount) || 0;
  const paid = parseFloat(amountPaid) || 0;
  return roundToDecimal(total - paid);
};